import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import CtqCommentsSideBar from '@pages-clean/risk-assessment/critical-to-quality-factors/sections/ctq-comments-sidebar';
import Factors from '@pages-clean/risk-assessment/critical-to-quality-factors/sections/ctq-factors';
import FunctionalDataReviews from '@pages-clean/risk-assessment/critical-to-quality-factors/sections/functional-data-reviews';
import WidgetsSection from '@pages-clean/risk-assessment/critical-to-quality-factors/sections/widgets/widgets';
import { RisksReviewsChipValues } from '@pages-clean/risk-assessment/critical-to-quality-factors/types';
import { IFormData, CTQFactorsContext, SelectionState } from '@pages-clean/risk-assessment/interfaces/context';
import { NullableStudy } from '@contexts/local-state-context';
import './index.scss';
import { Permissions, UserContext } from '@contexts/user-context';
import Constants from '@utils/constants';
import Modules from '@config/modules';
import { LoggedInPageProps } from '@config/routes/interfaces';

interface ICTQFactorsWrapperProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: ICTQFactorsWrapperProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges } = useUnsavedChangesContext();
    const { state, dispatch } = usePageToolbar();
    const [tabIndex, setTabIndex] = useState(0);
    const { user } = useContext(UserContext);

    const hasCommentsPageAccess = user?.can?.({
        module: Modules.RiskAssessment.value,
        element: Constants.PageElements.CtqComments,
        permission: Permissions.Enabled,
    });

    const showCommentsSidebar = state.commentsToggle && tabIndex === 0 && hasCommentsPageAccess;

    const [selectionState, setSelectionState] = useState<SelectionState>({
        selectedItemsCount: 0,
        bulkUpdateRows: [],
    });
    const [formData, setFormData] = useState<IFormData>({
        study,
        selectionState,
        riskGridIsFiltered: false,
        functionalDataReviewsGridIsFiltered: false,
        selectedChildTab: RisksReviewsChipValues.Risks,
    });

    const ctqFactorsProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setSelectionState,
        }),
        [formData, setFormData, selectionState],
    );

    const { t } = useTranslation('ram', { keyPrefix: 'ctqf' });

    useEffect(() => {
        setFormData({
            ...formData,
            study,
            selectionState,
        });
    }, [study?.id, selectionState]);

    return (
        <CTQFactorsContext.Provider value={ctqFactorsProviderValue}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: showCommentsSidebar ? 'calc(100% - 430px) auto' : '100%',
                }}
            >
                <div className="ctqf-container">
                    <WidgetsSection />
                    <div className="ctq-factors">
                        <TabStripLowDom
                            data={[
                                {
                                    title: t('tabs.ctqFactors'),
                                    content: <Factors />,
                                    dataTestId: 'critical-to-quality-factors-tab',
                                },
                                {
                                    title: t('tabs.functionalDataReviews'),
                                    content: <FunctionalDataReviews />,
                                    dataTestId: 'functional-data-reviews-tab',
                                },
                            ]}
                            canChangeTab={async () => {
                                if (anyComponentHasChanges()) {
                                    const confirmed = await confirmDiscardChanges();
                                    if (!confirmed) {
                                        return false;
                                    }
                                    discardChanges();
                                }
                                return true;
                            }}
                            onSelectedTabIndexChanged={(index) => {
                                if (hasCommentsPageAccess) {
                                    if (index === 0) {
                                        dispatch({
                                            type: 'showCommentsToggle',
                                            ...state,
                                        });
                                    } else {
                                        dispatch({
                                            type: 'hideCommentsToggle',
                                            ...state,
                                        });
                                    }

                                    setTabIndex(index);
                                }
                            }}
                        />
                    </div>
                </div>
                {showCommentsSidebar && (
                    <div style={{ display: 'flex', height: 'calc(-128px + 100vh)' }}>
                        <CtqCommentsSideBar />
                    </div>
                )}
            </div>
        </CTQFactorsContext.Provider>
    );
};

const CTQFactorsWrapper = ({ study }: ICTQFactorsWrapperProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const CTQFactors = ({ study }: LoggedInPageProps) => <CTQFactorsWrapper study={study} />;

export default CTQFactors;
